// src/actions/updateExpiredVaccines/UpdateExpiredVaccines.ts

import { ThunkAction } from 'redux-thunk';
import axios, { AxiosResponse } from 'axios';
import { showError, showSuccess } from '@spike/notifications-action';
import {
    CLIENT_FETCH_START,
    CLIENT_FETCH_SUCCESS,
    CLIENT_FETCH_ERROR,
    UPDATE_VACCINES_SAVE_START,
    UPDATE_VACCINES_SAVE_SUCCESS,
    UPDATE_VACCINES_SAVE_ERROR,
    UPDATE_VACCINES_CLEAR_SAVE_ERROR,
    UpdateVaccinesAction
} from './UpdateExpiredVaccinesActionTypes';
import ApiClient from 'api';
import { RootState } from 'store';
import { ClientExpiredVaccinesDto } from './UpdateExpiredVaccinesDto';
import Pet from '@spike/pet-model';
import { classifyVaccineRecordError, VaccineRecordErrorVariant } from '@spike/pets-action';
import { convertToSavePetDto } from './PetDataConverter';
import { serialize } from 'object-to-formdata';
import { PetVaccine } from '@spike/medical-history-model';
import { createMultipartTokenConfig } from 'api/ApiClient';

const HTTP_STATUS_401_UNAUTHORIZED = 401;
const base_url = `${process.env.REACT_APP_HOST_URL}`;
const getCustomerLinkUrl = base_url + '/api/public/v1/pet_expired_vaccines/';
const savePetVaccineLinkUrl = base_url + '/api/public/v1/pets/';

export const getClient = (uuid?: string | number): ThunkAction<void, RootState, unknown, any> => {
    return async dispatch => {
        dispatch(getClientStart());

        try {
            const response: AxiosResponse<ClientExpiredVaccinesDto> = await ApiClient.post(getCustomerLinkUrl, {
                uuid: uuid
            });
            dispatch(getClientSuccess(response.data));
        } catch (apiError) {
            dispatch(error('Error get client.'));
            dispatch(showError('Error get client.'));
        }
    };
};

const getClientStart = (): UpdateVaccinesAction => ({
    type: CLIENT_FETCH_START
});

const getClientSuccess = (clientExpiredVaccinesDto: ClientExpiredVaccinesDto): UpdateVaccinesAction => {
    return {
        type: CLIENT_FETCH_SUCCESS,
        payload: { loading: false, clientExpiredVaccines: clientExpiredVaccinesDto }
    };
};

export const error = (error: string): UpdateVaccinesAction => ({
    type: CLIENT_FETCH_ERROR,
    payload: { loading: false, fetchError: error }
});

export const newPetVaccines = (
    pet: Pet,
    newVaccines: Array<PetVaccine>,
    token: string,
    marketplaceId?: number
): ThunkAction<void, RootState, unknown, any> => {
    return async dispatch => {
        dispatch(saveStart());

        try {
            const saveUrl = `${savePetVaccineLinkUrl}${pet.id}`;

            const petRequest = convertToSavePetDto(pet, newVaccines, marketplaceId!);

            let formData = new FormData();

            const options = {
                indices: false,
                nullsAsUndefineds: false,
                booleansAsIntegers: false,
                allowEmptyArrays: false
            };

            formData = serialize(petRequest, options, formData, 'pet');

            await ApiClient.patch(saveUrl, formData, createMultipartTokenConfig(token));
            dispatch(saveSuccess());
            dispatch(showSuccess('Pet updated! 🎉'));
        } catch (apiError: unknown) {
            console.error(apiError);

            const responseData: unknown =
                axios.isAxiosError(apiError) && apiError.response ? apiError.response.data : undefined;

            const variant = classifyVaccineRecordError(responseData);

            if (variant) {
                // The modal is the only feedback for a recognized vaccine-record
                // failure, so no toast is dispatched here.
                dispatch(saveError(variant));
                return;
            }

            dispatch(saveError());

            const isValidationResponse = axios.isAxiosError(apiError) && apiError.response?.status === 422;

            const fieldMessages =
                isValidationResponse && responseData && typeof responseData === 'object'
                    ? Object.entries(responseData as Record<string, unknown>).map(
                          ([field, detail]) => `${field}: ${detail}`
                      )
                    : [];

            if (fieldMessages.length > 0) {
                fieldMessages.forEach(message => dispatch(showError(message)));
                return;
            }

            // Covers non-axios throws, network errors with no response, and
            // validation responses with no field detail — all of which
            // previously produced no feedback and left the save state hanging.
            dispatch(showError('Error update vaccines.'));
        }
    };
};

const saveStart = (): UpdateVaccinesAction => ({
    type: UPDATE_VACCINES_SAVE_START
});

const saveSuccess = (): UpdateVaccinesAction => {
    return {
        type: UPDATE_VACCINES_SAVE_SUCCESS,
        payload: { loading: false }
    };
};

const saveError = (variant?: VaccineRecordErrorVariant): UpdateVaccinesAction => ({
    type: UPDATE_VACCINES_SAVE_ERROR,
    payload: { variant }
});

export const clearSaveError = (): UpdateVaccinesAction => ({
    type: UPDATE_VACCINES_CLEAR_SAVE_ERROR
});
